import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import JsonLd from "../../components/base/JsonLd";
import { CategoryTag, SourceBadge } from "../../components/feature/ArticleCard";
import { articles, platforms, siteMeta } from "../../mocks/gossip";
import type { Article } from "../../types";
import useSeo from "../../hooks/useSeo";

const BASE = "https://qihang77.cn";

function platformName(source: string) {
  return platforms.find((p) => p.slug === source)?.name ?? "明星爆料";
}

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const article = useMemo<Article | undefined>(() => articles.find((a) => a.id === id), [id]);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: article ? `${article.title}-明星爆料` : "内容不存在-明星爆料",
    description: article ? article.summary : "您访问的页面不存在。",
    keywords: article ? `明星爆料,${article.category},${article.tags.slice(0, 3).join(",")}` : "明星爆料",
    path: `/article/${id}`,
    type: "article",
  });

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="bg-background-50 min-h-[60vh] flex items-center justify-center px-4 pt-20">
          <div className="text-center">
            <i className="ri-question-line text-4xl text-foreground-300" aria-hidden="true"></i>
            <h1 className="font-heading font-bold text-xl mt-4 text-foreground-900">这条瓜不存在或被删除了</h1>
            <p className="text-foreground-500 text-sm mt-2">它可能已经下线，或链接有误。</p>
            <Link to="/" className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary-600 text-background-50 px-5 py-2.5 text-sm font-medium hover:bg-primary-700 whitespace-nowrap">
              <i className="ri-home-4-line" aria-hidden="true"></i> 回到首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const related = articles.filter((a) => a.id !== article.id && a.id !== "gua-006" && a.id !== "gua-008").slice(0, 3);

  const crumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: BASE },
      { "@type": "ListItem", position: 2, name: article.sourceName, item: `${BASE}/channel/${article.source}` },
      { "@type": "ListItem", position: 3, name: article.title, item: `${BASE}/article/${article.id}` },
    ],
  };
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.summary,
    image: article.cover,
    articleSection: article.category,
    keywords: [...article.tags, "明星爆料"].join(","),
    inLanguage: "zh-CN",
    author: { "@type": "Person", name: article.author },
    publisher: { "@type": "Organization", name: siteMeta.name },
    url: `${BASE}/article/${article.id}`,
    mainEntityOfPage: `${BASE}/article/${article.id}`,
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setShareOpen(false);
    }
  };

  return (
    <>
      <JsonLd data={[crumb, blogSchema]} />
      <Navbar />
      <main className="bg-background-50 pt-16 md:pt-[72px]">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <nav aria-label="面包屑" className="py-5">
            <ol className="flex items-center gap-1.5 text-xs text-foreground-400 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line text-sm" /></li>
              <li><Link to={`/channel/${article.source}`} className="hover:text-primary-600 whitespace-nowrap">{article.sourceName}</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line text-sm" /></li>
              <li className="text-foreground-700 line-clamp-1 max-w-[12rem] sm:max-w-none">{article.title}</li>
            </ol>
          </nav>

          <article>
            <header className="mb-6">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <CategoryTag label={article.category} />
                {article.hot && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-primary-50 text-primary-700 px-2 py-0.5 text-xs font-medium">
                    <i className="ri-fire-fill text-sm" aria-hidden="true"></i> 高热
                  </span>
                )}
                <SourceBadge name={article.sourceName} />
              </div>
              <h1 className="font-heading font-black text-foreground-950 text-2xl md:text-4xl leading-tight tracking-tight">{article.title}</h1>
              <p className="text-foreground-500 text-sm md:text-base mt-4 leading-relaxed">{article.summary}</p>
              <div className="flex items-center justify-between mt-5 flex-wrap gap-3 border-y border-background-200 py-3">
                <div className="flex items-center gap-3 text-sm">
                  <span className="w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center font-bold">{article.author.slice(0, 1)}</span>
                  <span className="text-foreground-700 font-medium whitespace-nowrap">{article.author}</span>
                  <span className="text-foreground-400 text-xs whitespace-nowrap">· {article.time}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setLiked((v) => !v)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm transition cursor-pointer whitespace-nowrap ${
                      liked ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    <i className={`${liked ? "ri-heart-fill" : "ri-heart-line"} text-base`} aria-hidden="true"></i>
                    {article.likes + (liked ? 1 : 0)}
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShareOpen((v) => !v)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 px-3.5 py-2 text-sm transition cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-share-forward-line text-base" aria-hidden="true"></i> 分享
                    </button>
                    {shareOpen && (
                      <div className="absolute right-0 top-full mt-2 z-20 w-44 rounded-xl bg-background-50 border border-background-200 p-2 shadow-sm">
                        <button type="button" onClick={onCopy} className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap">
                          <i className="ri-link text-primary-500" aria-hidden="true"></i>{copied ? "已复制链接" : "复制链接"}
                        </button>
                        <a href={`https://service.weibo.com/share/share.php?title=${encodeURIComponent(article.title)}`} target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 whitespace-nowrap">
                          <i className="ri-weibo-line text-primary-600" aria-hidden="true"></i> 分享到微博
                        </a>
                        <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}`} target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 whitespace-nowrap">
                          <i className="ri-twitter-x-line text-foreground-800" aria-hidden="true"></i> 分享到 X
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </header>

            <div className="rounded-2xl overflow-hidden mb-8 bg-background-100">
              <div className="relative aspect-[16/9] w-full bg-background-200">
                <img src={article.cover} alt={article.title} title={`${article.title} 明星爆料`} className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </div>

            {/* 事件时间线（若有） */}
            {article.timeline && (
              <section className="rounded-2xl bg-background-100 p-5 md:p-6 mb-8">
                <h2 className="font-heading font-bold text-foreground-900 text-base flex items-center gap-2 mb-4">
                  <i className="ri-time-line text-primary-600 text-lg" aria-hidden="true"></i> 事件进展时间线
                </h2>
                <ol className="relative border-l border-background-300 ml-2 space-y-4">
                  {article.timeline.map((t, i) => (
                    <li key={i} className="relative pl-6">
                      <span className={`absolute -left-[7px] top-1 w-3.5 h-3.5 rounded-full border-2 ${i === 0 ? "bg-primary-600 border-primary-200" : "bg-background-100 border-primary-300"}`}></span>
                      <p className="text-xs font-bold text-primary-700 whitespace-nowrap">{t.t}</p>
                      <p className="text-sm text-foreground-700 leading-relaxed mt-1">{t.d}</p>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            <div className="space-y-5">
              {article.paragraphs.map((p, i) => (
                <p key={i} className="text-foreground-800 text-[15px] md:text-base leading-[1.9] text-justify">{p}</p>
              ))}
            </div>

            <aside className="rounded-xl bg-accent-50 border border-accent-200 p-4 md:p-5 my-7">
              <div className="flex gap-3">
                <i className="ri-information-line text-accent-700 text-xl shrink-0" aria-hidden="true"></i>
                <p className="text-xs text-accent-900 leading-relaxed">
                  温馨提示：本条内容为<strong>演示虚构素材</strong>，聚合自{article.sourceName}等公开讨论平台，仅作信息聚合展示，不代表本站立场。爆料有真有假，请理性看待网传信息，以当事人及官方声明为准。若涉及权益问题，欢迎联系我们处理。
                </p>
              </div>
            </aside>

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((t) => (
                <span key={t} className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-500 whitespace-nowrap"># {t}</span>
              ))}
            </div>
          </article>

          {/* 作者与来源 */}
          <section className="flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-2xl bg-background-100 p-5 mb-8">
            <span className="w-14 h-14 shrink-0 rounded-2xl bg-accent-500 text-background-50 flex items-center justify-center text-xl font-bold">{article.author.slice(0, 1)}</span>
            <div className="min-w-0 flex-1">
              <p className="font-heading font-bold text-foreground-900">{article.author}</p>
              <p className="text-xs text-foreground-500 mt-1 leading-relaxed">明星爆料的签约作者，专注{platformName(article.source)}等平台的明星动态追踪与时间线梳理。</p>
            </div>
            <Link to={`/channel/${article.source}`} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-primary-600 text-background-50 px-4 py-2 text-sm font-medium hover:bg-primary-700 whitespace-nowrap">
              查看该频道 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </section>
        </div>

        {/* 相关推荐 */}
        <section className="bg-background-100/70 border-y border-background-200">
          <div className="mx-auto max-w-7xl px-4 md:px-8 py-10 md:py-12">
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl flex items-center gap-2">
                <i className="ri-refresh-line text-primary-600 text-xl" aria-hidden="true"></i> 相关推荐 · 继续吃瓜
              </h2>
              <Link to="/#feed" className="hidden md:inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
                更多爆料 <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((a) => (
                <Link key={a.id} to={`/article/${a.id}`} className="group flex flex-col rounded-xl bg-background-50 overflow-hidden hover:-translate-y-1 transition">
                  <div className="relative aspect-[16/9] overflow-hidden bg-background-200">
                    <img src={a.cover} alt={a.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CategoryTag label={a.category} />
                      <span className="text-xs text-foreground-400">{a.time}</span>
                    </div>
                    <p className="font-heading font-bold text-foreground-900 text-sm leading-snug line-clamp-2 group-hover:text-primary-700 transition">{a.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}